import { useTranslation } from 'react-i18next';
import { chatImage } from '@anytag/shared-assets';
import { ChartsContainer, NoDataTitle, SplitButton } from '@anytag/shared-atoms';
import { PostingHabits, AverageEngagement, SectionWithHeader, PieChart } from '@anytag/shared-molecules';
import { useXhsProfileEngagementInsightsQuery, XhsPostType } from '@anytag/graphql';
import { useParamsSearch } from '@anytag/shared-hooks';
import { PIE_CHART_COLORS } from '../InfluencerDouyinProfile/utils';
import { getXhsAvgEngagement } from './utils';

interface InfluencerXhsProfileEngagementInsightsProps {
  influencerProfileId: number;
  socialAccountId: number;
}

const CHART_HEIGHT = 346;

export const InfluencerXhsProfileEngagementInsights = ({
  influencerProfileId,
  socialAccountId
}: InfluencerXhsProfileEngagementInsightsProps) => {
  const { t } = useTranslation();

  const { filter, setFilter } = useParamsSearch();

  const postType = filter?.xhsPostType || XhsPostType.NORMAL;

  const { data } = useXhsProfileEngagementInsightsQuery({
    variables: {
      pk: influencerProfileId,
      postType,
      socialAccountId: socialAccountId
    }
  });

  const handleXhsPostTypeChange = (xhsPostType: XhsPostType) => {
    setFilter({ ...filter, xhsPostType }, { resetScroll: false });
  };

  const xhsProfilePosts = data?.xhsProfileEngagementInsights;

  return (
    <SectionWithHeader
      title={t('Engagement Insights')}
      headerChildren={
        <SplitButton
          options={[
            {
              title: t('Button.Normal'),
              variant: 'outline',
              isActive: postType === XhsPostType.NORMAL,
              onClick: () => handleXhsPostTypeChange(XhsPostType.NORMAL)
            },
            {
              title: t('Button.Sponsored'),
              variant: 'outline',
              isActive: postType === XhsPostType.SPONSORED,
              onClick: () => handleXhsPostTypeChange(XhsPostType.SPONSORED)
            }
          ]}
          className="ml-auto"
        />
      }
      prefixIcon={chatImage}
      className="mt-4"
    >
      <ChartsContainer
        className="h-[392px]"
        elements={[
          <AverageEngagement data={getXhsAvgEngagement({ xhsProfilePosts, t })} title="Videos Average Engagement" />,

          <PieChart
            dataNotAvailable={!xhsProfilePosts?.postCategories?.length}
            header={<NoDataTitle title={t('Post Categories')} />}
            chart={{ type: 'pie', height: CHART_HEIGHT, spacingBottom: 0 }}
            data={
              xhsProfilePosts?.postCategories?.map((vt, i) => ({
                y: vt.rate,
                name: vt.name,
                color: PIE_CHART_COLORS[i] || ''
              })) || []
            }
            className="p-[10px_8px_16px]"
          />,
          <PostingHabits data={xhsProfilePosts?.postsHabit} height={CHART_HEIGHT} />
        ]}
      />
    </SectionWithHeader>
  );
};
